import React, { useState } from 'react';
import { IconButton, Tooltip, CircularProgress } from '@mui/material';
import {
  ErrorOutline,
  WarningAmberOutlined,
  InfoOutlined,
  Close,
  ExpandMore,
  ExpandLess,
  KeyboardArrowDown,
  KeyboardArrowRight,
} from '@mui/icons-material';
import { useActiveNotifications, useDismissNotification } from '../../hooks/useNotifications';
import type { Notification } from '../../services/notificationService';
import { NotificationDataView } from './NotificationDataView';

interface Props {
  colorsMap: Record<string, string>;
}

const severityConfig: Record<string, { icon: React.ReactNode; color: string; leftBorder: string; rowBg: string }> = {
  critical: {
    icon: <ErrorOutline sx={{ fontSize: 14, color: '#ef4444' }} />,
    color: 'text-red-600',
    leftBorder: '#ef4444',
    rowBg: '#ffffff',
  },
  warning: {
    icon: <WarningAmberOutlined sx={{ fontSize: 14, color: '#f59e0b' }} />,
    color: 'text-amber-500',
    leftBorder: '#f59e0b',
    rowBg: '#ffffff',
  },
  info: {
    icon: <InfoOutlined sx={{ fontSize: 14, color: '#3b82f6' }} />,
    color: 'text-blue-500',
    leftBorder: '#3b82f6',
    rowBg: '#ffffff',
  },
};

function timeAgo(dateStr: string): string {
  const diff = Date.now() - new Date(dateStr).getTime();
  const minutes = Math.floor(diff / 60000);
  if (minutes < 1) return 'just now';
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  return `${days}d ago`;
}

const AlertItem: React.FC<{
  notification: Notification;
  formulaColor?: string;
  onDismiss: (id: number) => void;
  isDismissing: boolean;
}> = ({ notification, formulaColor, onDismiss, isDismissing }) => {
  const config = severityConfig[notification.severity] || severityConfig.info;
  const [expanded, setExpanded] = useState(false);
  const isLong = notification.message.length > 60;

  return (
    <div
      className={`flex border-b border-gray-100 cursor-pointer hover:bg-gray-50 ${isDismissing ? 'opacity-50' : ''}`}
      style={{ borderLeft: `3px solid ${formulaColor || config.leftBorder}` }}
      onClick={() => setExpanded(!expanded)}
    >
      <div className="flex gap-2 p-2 flex-1 min-w-0">
        <div className="flex-shrink-0 mt-0.5">
          {config.icon}
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-start justify-between gap-1">
            <p className="text-sm font-medium text-gray-900 leading-tight">
              {(() => {
                const name = notification.formula_name || notification.formula_code || '';
                const title = notification.title;
                return title.startsWith(name + ':') ? title.slice(name.length + 1).trim()
                  : title.startsWith(name) ? title.slice(name.length).replace(/^[:\s-]+/, '').trim()
                  : title;
              })()}
            </p>
            <IconButton
              size="small"
              onClick={(e) => { e.stopPropagation(); onDismiss(notification.id); }}
              disabled={isDismissing}
              sx={{ padding: '1px' }}
            >
              <Close sx={{ fontSize: 12, color: '#9ca3af' }} />
            </IconButton>
          </div>
          <div className="flex items-center gap-1.5 mt-1">
            {(notification.formula_name || notification.formula_code) && (
              <span
                className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-semibold"
                style={{
                  backgroundColor: formulaColor ? `${formulaColor}18` : '#f3f4f6',
                  color: formulaColor || '#6b7280',
                }}
              >
                {notification.formula_name || notification.formula_code}
              </span>
            )}
            <span className="text-[10px] text-gray-400">{timeAgo(notification.created_at)}</span>
          </div>
          <NotificationDataView data={notification.data} />
          {expanded && (
            <p className="text-[11px] text-gray-600 leading-relaxed mt-1">
              {notification.message}
            </p>
          )}
        </div>
      </div>
    </div>
  );
};

export const StrategyAlertsList: React.FC<Props> = ({ colorsMap }) => {
  const { data: notifications = [], isLoading } = useActiveNotifications();
  const dismissMutation = useDismissNotification();
  const [isExpanded, setIsExpanded] = useState(true);

  if (isLoading || notifications.length === 0) return null;

  return (
    <div className="flex flex-col h-full bg-white shadow-md rounded overflow-hidden">
      <div
        onClick={() => setIsExpanded(!isExpanded)}
        className="flex-shrink-0 flex items-center justify-between cursor-pointer border-b border-gray-200 bg-gray-50 px-2 py-1.5"
      >
        <div className="flex items-center gap-1.5">
          {isExpanded
            ? <KeyboardArrowDown sx={{ fontSize: 16, color: '#6b7280' }} />
            : <KeyboardArrowRight sx={{ fontSize: 16, color: '#6b7280' }} />
          }
          <span className="text-xs font-semibold text-gray-700">Strategy Alerts</span>
          <span className="inline-flex items-center justify-center min-w-[16px] h-4 px-1 rounded-full bg-red-100 text-red-700 text-[9px] font-semibold">
            {notifications.length}
          </span>
        </div>
      </div>
      {isExpanded && (
        <div className="flex-1 overflow-auto">
          {notifications.map((n) => (
            <AlertItem
              key={n.id}
              notification={n}
              formulaColor={n.formula_code ? colorsMap[n.formula_code] : undefined}
              onDismiss={(id) => dismissMutation.mutate(id)}
              isDismissing={dismissMutation.isLoading && dismissMutation.variables === n.id}
            />
          ))}
        </div>
      )}
    </div>
  );
};
